import { Listing } from '@/components/Listing';
import Page, {
  PageHeader,
  PageHeaderActions,
  PageHeaderContent,
  PageHeaderDescription,
  PageHeaderTitle,
} from '@/components/Page';
import {
  useCourses,
  useCreateCourse,
  useDeleteCourse,
  useUpdateCourse,
  type Course,
} from '@/hooks/useCourses';
import { usePaginationState } from '@/hooks/usePaginationState';
import type { AppContext } from '@/main';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  type ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  useReactTable,
} from '@tanstack/react-table';
import {
  AvatarBeam,
  Badge,
  CourseDialog,
  DeleteCourseDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemFooter,
  ItemMedia,
  ItemTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ui/components';
import { Button } from '@ui/components/button';
import { format, formatDistanceToNow } from 'date-fns';
import { Book, BookMarked, ClockPlus, Edit2, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';

export const Route = createFileRoute('/_dashboard/courses/')<AppContext>({
  component: RouteComponent,
  loader: () => ({
    crumb: 'Courses',
  }),
});

function RouteComponent() {
  const { pagination, setPagination, orderDirection, setOrderDirection, orderBy, setOrderBy } =
    usePaginationState<'name' | 'createdAt' | 'updatedAt'>({
      defaultOrder: 'createdAt',
    });

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | undefined>(undefined);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null as string | null,
    name: '',
  });

  const {
    data: coursesResponse,
    isLoading,
    error,
  } = useCourses({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    orderBy,
    orderDirection,
  });
  const courses = coursesResponse?.items || [];

  const table = useCoursesTable({
    courses,
    setEditingCourse,
    setDeleteDialog,
    pagination,
    setPagination,
    rowCount: coursesResponse?.totalItems || 0,
  });

  const createCourseMutation = useCreateCourse();
  const updateCourseMutation = useUpdateCourse();
  const deleteCourseMutation = useDeleteCourse();

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-destructive">Failed to load courses</div>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <Page>
      <PageHeader>
        <PageHeaderContent>
          <PageHeaderTitle>Courses</PageHeaderTitle>
          <PageHeaderDescription>
            Manage your courses and curriculum
            {coursesResponse && <span className="ml-2">({coursesResponse.totalItems} total)</span>}
          </PageHeaderDescription>
        </PageHeaderContent>
        <PageHeaderActions>
          <Button onClick={() => setIsCreateDialogOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Course
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <Listing.Root
        values={courses}
        CardComponent={CourseCardComponent}
        table={table}
        isLoading={isLoading}
        totalItems={coursesResponse?.totalItems || 0}
        onDelete={(item: Course) => {
          setDeleteDialog({
            open: true,
            id: item.id,
            name: item.name,
          });
        }}
        onEdit={(item: Course) => {
          setEditingCourse(item);
        }}
      >
        <Listing.Header.Root>
          <Listing.Header.StatusFilter
            active="all"
            options={[
              {
                label: 'All courses',
                value: 'all',
              },
            ]}
            default="all"
            onChange={() => {}}
          />
          <Listing.Header.Selector.Root>
            <Listing.Header.Selector.View />
            <Listing.Header.Selector.OrderBy
              default="name"
              onChange={(value) => {
                const [orderBy, orderDirection] = value.split('-');
                setOrderBy(orderBy as 'name' | 'createdAt' | 'updatedAt');
                setOrderDirection(orderDirection as 'asc' | 'desc');
              }}
              options={[
                {
                  label: 'Name',
                  value: 'name',
                },
                {
                  label: 'Created At',
                  value: 'createdAt',
                },
                {
                  label: 'Updated At',
                  value: 'updateAt',
                },
              ]}
              orderBy={orderBy}
              orderDirection={orderDirection}
            />
            <Listing.Header.Selector.PageSize
              value={pagination.pageSize.toString()}
              onChange={(value) => setPagination({ ...pagination, pageSize: parseInt(value, 10) })}
            />
          </Listing.Header.Selector.Root>
        </Listing.Header.Root>
      </Listing.Root>

      {/* Dialogs */}
      <CourseDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSave={async (data: { name: string; description: string }) => {
          await createCourseMutation.mutateAsync(data);
        }}
      />

      <CourseDialog
        open={!!editingCourse}
        onOpenChange={(open) => !open && setEditingCourse(undefined)}
        course={editingCourse}
        onSave={async (data: { name: string; description: string }) => {
          if (!editingCourse) return;
          await updateCourseMutation.mutateAsync({
            courseId: editingCourse.id,
            data,
          });
        }}
      />

      <DeleteCourseDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog((prev) => ({ ...prev, open }))}
        courseId={deleteDialog.id}
        courseName={deleteDialog.name}
        onDelete={async (courseId: string) => {
          await deleteCourseMutation.mutateAsync(courseId);
        }}
      />
    </Page>
  );
}

function CourseCardComponent({
  item,
  onDelete,
  onEdit,
}: {
  item: Course;
  onDelete: (item: Course) => void;
  onEdit: (item: Course) => void;
}) {
  return (
    <Item
      variant="outline"
      key={item.id}
      className="bg-card text-card-foreground rounded-sm border py-6 shadow-sm hover:shadow-lg transition-shadow gap-2 pt-6"
    >
      <ItemMedia>
        <div className="bg-primary/10 text-primary rounded w-12 h-12 flex items-center justify-center">
          <BookMarked />
        </div>
      </ItemMedia>
      <ItemContent className="flex-col">
        <ItemTitle>
          <Link
            to={`/courses/$courseId`}
            params={{
              courseId: item.id,
            }}
            className="hover:underline"
          >
            {item.name}
          </Link>
        </ItemTitle>
        {item.description && <ItemDescription>{item.description}</ItemDescription>}
      </ItemContent>
      <ItemActions>
        <Tooltip>
          <TooltipTrigger>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <ClockPlus className="" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            Created&nbsp;
            {formatDistanceToNow(new Date(item.createdAt))} ago
          </TooltipContent>
        </Tooltip>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(item)}>
              <Edit2 className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onDelete(item)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </ItemActions>
      {/*<Separator variant="dashed" decorative={false} className="my-2" />*/}

      <ItemFooter className="text-sm gap-2 w-full grid grid-cols-2">
        <div className="flex flex-col items-center text-center justify-center p-4 rounded bg-background text-sm text-foreground gap-2 border border-dashed h-full">
          <Book className="w-6 h-6" />
          {item.exams.length || 0} Exams
        </div>
        <div className="flex flex-col items-center text-center justify-center p-4 rounded bg-background text-sm text-foreground gap-2 border border-dashed h-full">
          <div className="flex flex-row gap-2">
            {item.students.slice(0, 3).map((admin) => (
              <AvatarBeam id={admin.id ?? ''} className="size-8" name={admin.name} />
            ))}
            {item.students.length > 3 && (
              <div className="size-8 rounded bg-muted flex items-center justify-center -ml-6 border z-10 text-xs">
                +{item.students.length - 3}
              </div>
            )}
            {item.students.length === 0 && (
              <div className="size-8 rounded bg-muted flex items-center justify-center border z-0">
                <Tooltip>
                  <TooltipTrigger>
                    <span className="text-muted-foreground">'-'</span>
                  </TooltipTrigger>
                  <TooltipContent>No students enrolled</TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>
          <span>{item.students.length} Students</span>
        </div>
      </ItemFooter>
    </Item>
  );
}

function useCoursesTable({
  courses,
  setEditingCourse,
  setDeleteDialog,
  pagination,
  setPagination,
  rowCount,
}: {
  courses: Course[];
  setEditingCourse: (course: Course) => void;
  setDeleteDialog: (params: { open: boolean; id: string | null; name: string }) => void;
  pagination: PaginationState;
  setPagination: Dispatch<SetStateAction<PaginationState>>;
  rowCount: number;
}) {
  const columns = useMemo<ColumnDef<Course>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row: { original: item } }) => (
          <Link
            to="/courses/$courseId"
            params={{ courseId: item.id }}
            className="font-medium hover:underline"
          >
            {item.name}
          </Link>
        ),
      },
      {
        accessorKey: 'exams',
        header: 'Exams',
        cell: ({ row: { original: item } }) => (
          <Badge variant="secondary" className="font-mono">
            {item.exams?.length ?? '0'}
          </Badge>
        ),
      },
      {
        accessorKey: 'archived',
        header: 'Status',
        cell: ({
          row: {
            original: { archived },
          },
        }) => (
          <Badge variant={archived ? 'outline' : 'default'}>
            {archived ? 'Archived' : 'Active'}
          </Badge>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created at',
        cell: ({ row: { original: item } }) => (
          <div className="text-muted-foreground">
            {format(new Date(item.createdAt), 'MMM dd, yyyy')}
          </div>
        ),
      },
      {
        id: 'actions',
        enableHiding: false,
        header: 'Actions',
        cell: ({ row: { original: item } }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  console.log('Edit exam');
                  setEditingCourse(item);
                }}
              >
                <Edit2 className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  console.log('Delete exam');
                  if (item) {
                    setDeleteDialog({
                      open: true,
                      id: item.id,
                      name: item.name,
                    });
                  }
                }}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: courses,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
    manualPagination: true,
    rowCount,
  });

  return table;
}
